import { AgencyLookupStore } from 'app/shared/lookup/agency-lookup/agency-lookup.store';
import { DecimalPipe, UpperCasePipe } from '@angular/common';
import { Component, Input, NgZone, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DropdownItem } from 'app/event/models/dropdown-item';
import { NonCommissionType } from 'app/event/models/non-commission-type';
import { GroupPayConfigStore } from 'app/event/service/grouppay-config.store';
import { OoivRulesStore } from 'app/event/service/ooiv-rules.store';
import { TaxDeterminationStore } from 'app/event/service/tax-determination.store';
import { DateUtil } from 'app/shared/utils/date.util';
import { Observable } from 'rxjs/Observable';
import { catchError, finalize, map, take, takeUntil, tap } from 'rxjs/operators';
import { EnvironmentConfiguration } from '../../../environment/services/environment.configuration';
import { AuthenticationStore } from '../../../permission/services/authentication.store';
import { ConfirmationModalConfig } from '../../../shared/confirmation-modal/confirmation-modal-config';
import { ConfirmationModalComponent } from '../../../shared/confirmation-modal/confirmation-modal.component';
import { LogService } from '../../../shared/log/log.service';
import { ErrorHandlerService } from '../../../shared/utils/error-handler.service';
import { PioneerValidators } from '../../../shared/utils/pioneer.validators';
import { OnyxEvent } from '../../models/event';
import { Party } from '../../models/party';
import { Payment } from '../../models/payment';
import { PaymentType } from '../../models/payment-type';
import { AdjustmentStore } from '../../service/adjustment.store';
import { EventStore } from '../../service/event.store';
import { PaymentStore } from '../../service/payment.store';
import { EventUtils } from '../../utils/event-utils';
import { PaymentStatusUtils } from '../../utils/payment-status-utils';
import { PaymentStatus } from './../../models/payment-status.enum';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { Unsubscriber } from './../../../shared/helper/unsubscriber';
import { CalculatePaymentService } from 'app/event/service/calculate-payment.service';
import { ActivatedRoute } from '@angular/router';
import { FlashMessagesService } from '../../../shared/flash-messages/service/flash.messages.service';
import { AweStore } from 'app/event/service/awe.store';
import { PaymentSummary } from 'app/event/models/payment-summary/payment-summary';
import { PaymentSummaryStore } from 'app/event/service/payment-summary/payment-summary.store';


@Component({
  selector: 'pioneer-add-payment',
  templateUrl: './add-payment.component.html'
})
export class AddPaymentComponent extends Unsubscriber implements OnInit, OnChanges {

  @ViewChild('confirmationModal')
  confirmationModal: ConfirmationModalComponent;

  @ViewChild('approversModal')
  approversModal: ConfirmationModalComponent;

  paymentForm: FormGroup;
  adjustmentForm: FormGroup;
  eventForm: FormGroup;

  addPaymentEnabled = true;
  addPaymentError = false;
  isEventFinished = false;
  ooivRulesValidation = true;
  ooivEntityReady = true;
  finalPaymentHint: string;

  isPropertyUser: boolean;

  confirmationModalConfig = new ConfirmationModalConfig();

  approversModalConfig = new ConfirmationModalConfig();

  onyxEvent: OnyxEvent;

  nonCommissionTypes: DropdownItem[];
  cancelTypes: DropdownItem[];
  paidDirectTypes: DropdownItem[];

  public strikeThroughPrepayment = false;

  public numberOfApprovers = 0;
  public isSubscriberAgency = false;


  @Input()
  public prepayment: Payment = null;

  hasPayment: boolean;
  hasPrePayment: boolean;
  private isLastPaymentRequestedOrRequestResentOrRequestRejected: boolean;
  isLastPaymentPayRejected: boolean;

  applyOoiv = false;
  isNotPendingTaxEstimated = true;

  @Input()
  vendor: Party;

  calculatedPaymentWithTax: number;

  paymentSummary: PaymentSummary;
  approversList: any[];

  constructor(
    private formBuilder: FormBuilder,
    private paymentStore: PaymentStore,
    private aweStore: AweStore,
    private adjustmentStore: AdjustmentStore,
    private translateService: TranslateService,
    private environment: EnvironmentConfiguration,
    private errorHandlerService: ErrorHandlerService,
    private logService: LogService,
    private decimalPipe: DecimalPipe,
    private uppercasePipe: UpperCasePipe,
    private eventStore: EventStore,
    private authenticationStore: AuthenticationStore,
    public ngZone: NgZone,
    private taxDeterminationStore: TaxDeterminationStore,
    private groupPayConfigStore: GroupPayConfigStore,
    private ooivRulesStore: OoivRulesStore,
    private route: ActivatedRoute,
    private flashMessagesService: FlashMessagesService,
    private paymentSummaryStore: PaymentSummaryStore,
    private agencyLookupStore: AgencyLookupStore
  ) {
    super();
  }

  @Input('event')
  set event(event: OnyxEvent) {
    this.onyxEvent = event;
  }

  ngOnInit(): void {
    this.isPropertyUser = this.authenticationStore.isPropertyUser();

    this.isLastPaymentPayRejected = false;

    this.paymentForm = this.formBuilder.group({
      'description': ['', Validators.maxLength(255)],
      'type': [null],
      'cancelType': [null],
      'commissionAmount': [null, [PioneerValidators.isValidAmount]]
    });

    this.adjustmentForm = this.formBuilder.group({
      // defaults to undefined so that someone is required to enter an amount before submitting
      'adjustmentAmount': [undefined, [Validators.required, PioneerValidators.isValidAmount]]
    });

    this.eventForm = this.formBuilder.group({
      'masterBillPaid': ['']
    });

    if (!this.onyxEvent) {
      this.errorHandlerService.throw(
        'Input property onyxEvent is null in AddPaymentComponent',
        this.translateService.instant('MESSAGES.ERROR.INPUT_PROPERTY_NULL', {
          property: 'onyxEvent',
          className: 'AddPaymentComponent'
        })
      );
    }

    if (!this.vendor || !this.vendor.eventPartyId) {
      const message = this.translateService.instant('MESSAGES.ERROR.INPUT_PROPERTY_NULL', {
        property: 'vendor.eventPartyId',
        className: 'AddPaymentComponent'
      });
      this.errorHandlerService.throw('vendor.eventPartyId is null or undefined', message);
      return;
    }

    if (this.isPropertyUser) {
      // Get approvers count from AWE
      this.addSubscription(
        this.aweStore.getAweApprovers(this.onyxEvent.eventId, this.vendor.eventPartyId)
          .pipe(takeUntil(this._destroyed$))
          .subscribe((approvers: any[]) => {
            if (approvers) {
              this.numberOfApprovers = approvers.length;
              this.approversList = approvers;
            }
          })
      );

      this.addSubscription(
        this.agencyLookupStore
          .isSubscriberAgency(this.onyxEvent.eventAgency.code)
          .pipe(takeUntil(this._destroyed$))
          .subscribe((isSubscriber: boolean) => {
            this.isSubscriberAgency = !!isSubscriber;
          })
      );


    }




    this.paymentSummaryStore.getPaymentSummary(this.vendor.eventPartyId)
      .pipe(
        takeUntil(this._destroyed$),
        catchError(err => {
          this.errorHandlerService.throw(err.message, this.translateService.instant('MESSAGES.ERROR.COULD_NOT_GET_PAYMENT_SUMMARY'));
          return Observable.empty();
        })
      ).subscribe((paymentSummary: PaymentSummary) => {
        this.paymentSummary = paymentSummary;

        this.calculatedPaymentWithTax = paymentSummary.calculatedPayment;
        this.paymentForm.setValidators([PioneerValidators.commissionAmountAndTypeCrossValidator(this.calculatedPaymentWithTax)]);
        this.paymentForm.updateValueAndValidity();

        this.isLastPaymentRequestedOrRequestResentOrRequestRejected =
          this.paymentStore.isRequestedOrResentRequestOrRequestRejectedOrError(paymentSummary);
      });

    if (this.prepayment) {
      if (PaymentStatusUtils.isError(this.prepayment.status)) {
        this.strikeThroughPrepayment = true;
      }
      this.hasPayment = true;
    }

    this.checkEventDates();

    this.retrieveFinalPaymentHint();

    this.paymentStore.getVendorPayments(this.vendor.eventPartyId).take(1).subscribe(paymentList => {
      if (paymentList.size > 0) {
        this.hasPayment = true;
      }
    });

    this.addNonCommissionDueTypes();
    this.addCancelTypes();
    this.addPaidDirectTypes();

    this.addSubscription(this.paymentStore.isLastVendorPaymentPayRejected(this.vendor.eventPartyId).subscribe(isPayRejected => {
      this.isLastPaymentPayRejected = isPayRejected;
    }));

    this.prePaymentSubscription();

    this.groupPayConfigStore.isOoivEnabled(
      this.onyxEvent.eventAgency.code, this.onyxEvent.eventAgency.countryCode, this.vendor.countryCode).pipe(
        takeUntil(this._destroyed$),
        tap(
          (ooivEnabled: boolean) => this.applyOoiv = ooivEnabled
        )
      )
    .subscribe();

    this.addSubscription(this.ooivRulesStore.getVendorOoivExceptionRules(this.vendor.eventPartyId)
      .subscribe(rule => {
        if (rule) {
          this.ooivRulesValidation = rule.ooivExceptionsIsValid;
        }

        if (this.ooivRulesValidation) {
          this.ooivRulesStore.getAgencyOoivExceptionRule().subscribe(agencyRule => {
            if (agencyRule) {
              this.ooivEntityReady = agencyRule.ooivExceptionsIsValid;
            }
          });
        }
      }));

    this.addSubscription(this.taxDeterminationStore.getGlobalResponseTaxesList().subscribe((taxes) => {
      this.isNotPendingTaxEstimated = taxes.isValidTax;
    }));


    if (this.isInvalidNonSubscriberVendorEmailContact()) {
      this.flashMessagesService.warn('EVENT.VENDOR_INVALID_EMAIL',
        null, null);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {

    if (!changes['event']) {
      return;
    } else {
      this.retrieveFinalPaymentHint();
    }
  }

  isValidPaymentDate(): boolean {
    if (!this.onyxEvent || !this.getEventEndDate()) {
      return false;
    }

    const firstPayDate = new Date(this.getEventEndDate());
    firstPayDate.setDate(firstPayDate.getDate() + this.environment.payments.paymentDelayDaysAfterEventEnd + 1);

    const firstPayDateUtcSeconds = firstPayDate.getTime();
    const currentUtcSeconds = new Date().getTime();
    const isValid = currentUtcSeconds >= firstPayDateUtcSeconds;

    this.logService.verbose(`allow: ${firstPayDateUtcSeconds}, current: ${currentUtcSeconds}, isValid: ${isValid}`);

    return isValid;
  }

  get approvalButtonLabel(): string {
    const isSubscriber = this.isSubscriberAgency;
    const hasApprovers = this.numberOfApprovers > 0;

    if (this.authenticationStore.isAgencyUser()) {
      return this.translateService.instant('EVENT.COMMISSION.REQUEST_APPROVAL_PAYMENT');
    }

    if (!isSubscriber && hasApprovers) {
      return this.translateService.instant('EVENT.COMMISSION.REQUEST_APPROVAL_PAYMENT');
    }

    if (!isSubscriber && !hasApprovers) {
      return this.translateService.instant('EVENT.COMMISSION.AUTHORIZE_FINAL_PAYMENT');
    }

    if (isSubscriber) {
      return this.translateService.instant('EVENT.COMMISSION.REQUEST_APPROVAL_PAYMENT');
    }

    return this.translateService.instant('EVENT.COMMISSION.AUTHORIZE_FINAL_PAYMENT');
  }

  confirmBeforeApprovalWorkflow(): void {
    if (this.isMultiVendor()) {
      this.confirmBeforeAuthorizingPayment();
      return;
    }

    if (this.numberOfApprovers > 0) {
      this.openApprovalWorkflowModal(this.approversList);
    } else {
      this.confirmBeforeAuthorizingPayment();
    }
  }

  openApprovalWorkflowModal(approvers): void {
    // set up the title of the confirmation modal
    const titleLabel = 'EVENT.COMMISSION.REQUEST_PAYMENT_APPROVERS_TITLE';
    const contentLabel = 'EVENT.COMMISSION.REQUEST_PAYMENT_APPROVERS_CONTENT';
    const confirmLabel = 'EVENT.COMMISSION.REQUEST_PAYMENT_APPROVERS_CONFIRM';

    const params = {
      approvers: this.formatApprovers(approvers)
    };
    const title = this.translateService.instant(titleLabel);
    const content = this.translateService.instant(contentLabel, params);
    const confirmTitle = this.translateService.instant(confirmLabel);

    this.approversModalConfig.title = title;
    this.approversModalConfig.content = content;
    this.approversModalConfig.confirm = confirmTitle;

    this.approversModal.open();
  }

  formatApprovers(arr: string[]): string {
    return arr.map(item => '<li>' + item + '</li>').join('');
  }

  confirmBeforeAuthorizingPayment(): void {
    // set up the title of the confirmation modal
    const label = this.showRequestPayment()
      ? 'EVENT.COMMISSION.REQUEST_PAYMENT_CONFIRMATION_TITLE' : 'EVENT.COMMISSION.AUTHORIZE_PAYMENT_CONFIRMATION_TITLE';
    const confirmLabel = this.showRequestPayment()
      ? 'EVENT.COMMISSION.CONFIRMATION_REQUEST' : 'EVENT.COMMISSION.CONFIRMATION_CONFIRM';
    const params = {
      paymentAmount: this.decimalPipe.transform(this.calculatedPaymentWithTax, '1.2-2'),
      currency: this.uppercasePipe.transform(this.onyxEvent.currency)
    };
    const title = this.translateService.instant(label, params);
    const confirmTitle = this.translateService.instant(confirmLabel);
    this.confirmationModalConfig.title = title;
    this.confirmationModalConfig.confirm = confirmTitle;

    this.confirmationModal.open();
  }

  savePayment(pendingApproval = false): Observable<any> {
    const description = this.paymentForm.value.description;

    let totalVendorTaxes = 0;
    let eventPaymentTaxDetail;
    if (this.applyOoiv) {
      totalVendorTaxes = this.paymentSummary.estimatedTax;
      this.taxDeterminationStore.vendorTaxRates(this.vendor.eventPartyId, this.applyOoiv)
        .pipe(take(1))
        .subscribe(listRaxReponse => eventPaymentTaxDetail = listRaxReponse);
    }

    const payment = new Payment({
      commissionAmount: this.paymentSummary.commissionAmount,
      description: description,
      commissionCurrency: this.onyxEvent.currency,
      eventId: this.onyxEvent.eventId,
      type: PaymentType.FINAL_PAYMENT,
      eventPartyId: this.vendor.eventPartyId,
      totalTaxAmount: totalVendorTaxes,
      eventPaymentTaxDetail: eventPaymentTaxDetail,
      status: pendingApproval ? PaymentStatus.PENDING_WF_APPROVAL : undefined
    });

    this.addPaymentEnabled = false;
    let obs$: Observable<any>;
    if (this.paymentSummary
      && [PaymentStatus.REQUEST_REJECTED, PaymentStatus.REJECTED_WF_APPROVAL].some(
        status => status === this.paymentSummary.paymentStatus)) {
      obs$ = this.paymentStore.updateFinalPayment(payment);
    } else {
      obs$ = this.paymentStore.addPayment(payment);
    }

    obs$.pipe(
      take(1),
      catchError((error) => {
        this.addPaymentError = true;
        return Observable.throw(error);
      }),
      tap(() => {
        this.refreshPaymentData();

        if (!this.addPaymentError) {
          this.addPaymentEnabled = true;
        }
      })
    ).subscribe();

    return obs$;
  }

  startWorkflow() {
    this.savePayment(true);
  }

  isAddPaymentEnabled(): Observable<boolean> {
    return this.canAddPayment().map((canAddPayment: boolean) => {
      return canAddPayment && this.paymentForm && this.paymentForm.valid;
    });
  }

  canAddPayment(): Observable<boolean> {
    return this.eventStore.selectedEvent.flatMap((event: OnyxEvent) => {
      return this.paymentStore.isCurrentVendorPaymentValid(this.vendor.eventPartyId).map(currentPaymentValid => {
        return this.addPaymentEnabled &&
          this.calculatedPayment > 0 &&
          !currentPaymentValid &&
          this.isValidPaymentDate() &&
          EventUtils.doesEventHaveAgency(event) &&
          this.ooivRulesValidation &&
          this.ooivEntityReady;
      });
    });
  }

  resetPaymentForm(): void {
    this.paymentForm.reset({
      description: '',
      type: null,
      commissionAmount: undefined
    });
  }

  confirmBeforeAddPaymentHandler(value) {
    if (value) {
      this.savePayment();
    }
  }

  confirmBeforeApprovalWorkflowHandler(value) {
    if (value) {
      this.startWorkflow();
    }
  }

  public get calculatedPayment(): number {
    return this.paymentSummary ? this.paymentSummary.calculatedPayment : 0;
  }

  public isAuthorizedPayment() {
    return this.authenticationStore.hasPermission('AuthorizePayment') && this.isEventControlledByThisUserType();
  }

  public disablePaymentDescription() {
    return !this.isAuthorizedPayment() && !this.canCancelEvent();
  }

  public isEventControlledByThisUserType() {
    return (this.authenticationStore.isAgencyUser() && !this.vendor.subscriber) ||
      (this.isPropertyUser && this.vendor.subscriber);
  }

  public isEditAutoPayEnabled() {
    return this.authenticationStore.hasPermission('EditAutoPay') &&
      this.isPropertyUser && this.vendor.subscriber;
  }

  public isAutopayOn() {
    return this.onyxEvent.autoPayment;
  }

  checkEventDates() {
    const _endDate = EventUtils.getEventEndDate(this.onyxEvent, this.authenticationStore.isAgencyUser());
    this.isEventFinished = DateUtil.differenceInCalendarDays(DateUtil.parseDate(_endDate), new Date()) < 0;
  }

  public showRequestPayment(): boolean {
    if (this.isPropertyUser) {
      return true;
    } else {
      return this.authenticationStore.isAgencyUser() && !this.vendor.subscriber;
    }
  }

  public isNotMissingVendorEmailContact(): boolean {
    return (this.vendor.contact && this.vendor.contact.email && this.vendor.contact.email.trim().length > 0);
  }

  private retrieveFinalPaymentHint() {

    this.addSubscription(this.isAddPaymentEnabled().subscribe((isAddPayEnabled: boolean) => {
      if (!this.isNotMissingVendorEmailContact()) {
        this.finalPaymentHint = this.translateService.instant('MESSAGES.ERROR.VENDOR_EMAIL_MISSING');
      } else if (this.isInvalidNonSubscriberVendorEmailContact()) {
        this.finalPaymentHint = this.translateService.instant('MESSAGES.ERROR.VENDOR_EMAIL_INVALID');
      } else if (this.isAutopayOn()) {
        this.finalPaymentHint = this.translateService.instant('EVENT.COMMISSION.AUTO_PAY_ENABLED');
      } else if (this.isAuthorizedPayment() && isAddPayEnabled) {
        this.finalPaymentHint = this.translateService.instant('EVENT.COMMISSION.PAYMENTS_WILL_BE_PROCESSED');
      } else if (this.isAuthorizedPayment() && !isAddPayEnabled && !this.isEventFinished && !this.showRequestPayment()) {
        this.finalPaymentHint = this.translateService.instant('EVENT.COMMISSION.PAYMENT_OPTION_DISABLED');
      } else if (this.isAuthorizedPayment() && !isAddPayEnabled && !this.isEventFinished && this.showRequestPayment()) {
        this.finalPaymentHint = this.translateService.instant('EVENT.COMMISSION.PAYMENT_AFTER_EVENT_END_DATE');
      } else if (!this.isAuthorizedPayment()) {
        this.finalPaymentHint = this.translateService.instant('EVENT.COMMISSION.PAYMENT_OPTION_NOT_AUTHORIZED');
      } else {
        this.finalPaymentHint = null;
      }
    }));

  }

  showPaymentForm(): Observable<boolean> {
    return this.canAddPayment().map((canAddPayment: boolean) => {
      let showForm = !this.isAutopayOn() && this.isAuthorizedPayment() && canAddPayment;
      showForm = showForm || this.canCancelEvent();
      showForm = showForm || !this.hasPayment;
      showForm = showForm && this.showPayRejected() && !(this.isMultiVendor() && this.vendor.subscriber);
      if (!showForm) {
        this.unSelectNonCommType();
      }
      return showForm;
    });
  }

  canCancelEvent(): boolean {
    if (this.isPropertyUser) {
      return !this.hasPrePayment;
    } else {
      return !this.vendor.subscriber;
    }
  }

  canPaidDirectAndCancel(): boolean {
    return this.isLastPaymentRequestedOrRequestResentOrRequestRejected || !this.hasPayment;
  }

  showPayRejected(): boolean {
    return !this.isLastPaymentPayRejected;
  }

  isSubscriber(): boolean {
    return this.vendor.subscriber;
  }

  showPaidDirectCancel(): boolean {
    if ((this.canCancelEvent() || !this.isPropertyUser) && !this.hasPayment) {
      return true;
    }
    return false;
  }

  toggleMasterBillPaidSetting() {
    for (let i = 0; i < this.onyxEvent.eventVendors.length; i++) {
      let vendor = this.onyxEvent.eventVendors[i];
      const vendorCode = vendor.code ? vendor.code : vendor.chain + '|' + vendor.brand + '|' + vendor.propertyId;

      if (this.getVendorCode() === vendorCode) {
        vendor.masterBillPaid = !vendor.masterBillPaid;
      }
    }
    this.addSubscription(this.eventStore.updateEventIndicators(this.onyxEvent).subscribe());
  }

  getVendorCode(): string {
    return this.vendor.code ? this.vendor.code : this.vendor.chain + '|' + this.vendor.brand + '|' + this.vendor.propertyId;
  }

  addNonCommissionDueTypes(): void {
    if (this.authenticationStore.hasPermission('EditAutoPay') || this.authenticationStore.hasPermission('AuthorizePayment')) {
      this.nonCommissionTypes = [
        new DropdownItem(PaymentType.PAID_DIRECT, NonCommissionType.PAID_DIRECT),
        new DropdownItem(PaymentType.EVENT_CANCELLED, NonCommissionType.EVENT_CANCELLED)
      ];
    } else {
      this.nonCommissionTypes = [
        new DropdownItem(PaymentType.EVENT_CANCELLED, NonCommissionType.EVENT_CANCELLED)
      ];
    }
  }

  addCancelTypes(): void {
    this.cancelTypes = [
      new DropdownItem(PaymentType.EVENT_CANCELLED, NonCommissionType.EVENT_CANCELLED)
    ];
  }

  addPaidDirectTypes(): void {
    this.paidDirectTypes = [
      new DropdownItem(PaymentType.PAID_DIRECT, NonCommissionType.PAID_DIRECT)
    ];
  }

  isPayDirect(): boolean {
    return this.nonCommType && this.nonCommType.key === PaymentType.PAID_DIRECT;
  }

  get nonCommType(): DropdownItem {
    return this.getFormControlValue('type');
  }

  get cancelType(): DropdownItem {
    return this.getFormControlValue('cancelType');
  }

  get nonCommAmountPaid(): number {
    return this.getFormControlValue('commissionAmount');
  }

  private getFormControlValue(name: string): any {
    return this.paymentForm ? this.paymentForm.get(name).value : null;
  }

  isNonCommTypeSelected(): boolean {
    return this.nonCommType != null;
  }

  isCancelTypeSelected(): boolean {
    return this.cancelType != null;
  }

  unSelectNonCommType(): void {
    this.paymentForm.reset({
      description: this.paymentForm.get('description').value,
      type: null,
      cancelType: null,
      commissionAmount: null
    });
  }

  public saveNonCommPaymentType() {
    this.paymentForm.disable();

    const description = this.paymentForm.value.description;
    const paymentType = this.nonCommType.key;
    const commAmount = this.nonCommAmountPaid;
    const payment = this.createPayment(commAmount, description, paymentType);

    const obs$ = this.modifyPayment(payment).pipe(
      tap(() => this.refreshPaymentData()),
      catchError(() => {
        return Observable.of(null);
      }),
      finalize(() => {
        this.paymentForm.enable();
      })
    );
    obs$.subscribe();
    return obs$;
  }

  public saveCancelPaymentType() {
    this.paymentForm.disable();

    const description = this.paymentForm.value.description;
    const paymentType = this.cancelType.key;
    const commAmount = null;
    const payment = this.createPayment(commAmount, description, paymentType);

    const obs$ = this.modifyCancelPayment(payment).pipe(
      tap(() => this.refreshPaymentData()),
      catchError(() => {
        return Observable.of(null);
      }),
      finalize(() => {
        this.paymentForm.enable();
      })
    );
    obs$.subscribe();
    return obs$;
  }

  private createPayment(commAmount: any, description: any, paymentType: string) {
    return new Payment({
      commissionAmount: commAmount,
      description: description,
      commissionCurrency: this.onyxEvent.currency,
      eventId: this.onyxEvent.eventId,
      eventPartyId: this.vendor.eventPartyId,
      type: paymentType
    });
  }

  private refreshPaymentData() {
    this.resetPaymentForm();

    const eventId = this.onyxEvent.eventId;
    if (this.isMultiVendor()) {
      this.paymentStore.loadPayments(eventId).pipe(take(1)).subscribe();
      this.paymentStore.loadLatestFinalPayment(eventId).pipe(take(1)).subscribe();
      this.paymentStore.loadFinalPaymentStatusList(eventId).pipe(take(1)).subscribe();
      this.eventStore.getEvent(eventId).pipe(take(1)).subscribe();
      this.adjustmentStore.loadVendorsTotalPaymentsSummary(eventId).pipe(take(1)).subscribe();
    } else {
      const eventPartyId = this.vendor.eventPartyId;
      this.paymentStore.loadPaymentsByParty(eventId, eventPartyId).pipe(take(1)).subscribe();
      this.paymentStore.loadLatestFinalPaymentByParty(eventId, eventPartyId).pipe(take(1)).subscribe();
      this.paymentStore.loadFinalPaymentStatusListByParty(eventId, eventPartyId).pipe(take(1)).subscribe();
      this.eventStore.getEventByParty(eventId, eventPartyId).pipe(take(1)).subscribe();
      this.adjustmentStore.loadVendorsTotalPaymentsSummaryByParty(eventId, eventPartyId).pipe(take(1)).subscribe();
    }
  }

  private modifyPayment(payment: Payment): Observable<any> {
    this.addPaymentEnabled = false;
    const updatableStatuses = [
      PaymentStatus.REQUESTED,
      PaymentStatus.REQUEST_REJECTED,
      PaymentStatus.RESENT_REQUEST,
      PaymentStatus.REJECTED_WF_APPROVAL];
    if (this.paymentSummary && updatableStatuses.some(status => status === this.paymentSummary.paymentStatus)) {
      return this.paymentStore.updateFinalPayment(payment).finally(() => {
        this.addPaymentEnabled = true;
      });
    } else {
      return this.paymentStore.addPayment(payment).finally(() => {
        this.addPaymentEnabled = true;
      });
    }
  }

  private modifyCancelPayment(payment: Payment): Observable<any> {
    this.addPaymentEnabled = false;
    const cancelExistingStatuses = [PaymentStatus.REQUEST_REJECTED, PaymentStatus.REJECTED_WF_APPROVAL];
    if (this.paymentSummary && cancelExistingStatuses.some(status => status === this.paymentSummary.paymentStatus)) {
      return this.paymentStore.cancelExistingPayment(payment).finally(() => {
        this.addPaymentEnabled = true;
      });
    } else {
      return this.paymentStore.cancelNewPayment(payment).finally(() => {
        this.addPaymentEnabled = true;
      });
    }
  }

  getAmountPaidLabel(): string {
    const label = 'EVENT.COMMISSION.NON_COMM_AMOUNT_PAID';
    const params = {
      currency: this.uppercasePipe.transform(this.onyxEvent.currency)
    };
    return this.translateService.instant(label, params);
  }

  disablePaidDirect(): boolean {
    if (this.isPayDirect() && (!this.nonCommAmountPaid || (this.nonCommAmountPaid && this.nonCommAmountPaid < 0))) {
      return true;
    }
    if (!this.paymentForm.valid) {
      return true;
    }
    if (!this.paymentForm.get('commissionAmount').valid) {
      return true;
    }
    return false;
  }

  getNonCommDescriptionLabel(): string {
    let label = '';
    if (this.isMultiVendor()) {
      label = 'EVENT.COMMISSION.NON_COMM_DESCRIPTION_VENDOR';
    } else {
      label = 'EVENT.COMMISSION.NON_COMM_DESCRIPTION';
    }
    return this.translateService.instant(label);
  }

  isMultiVendor() {
    return this.authenticationStore.hasPermission('MultiVendor');
  }

  isCommissionGreaterThanZero() {
    return this.calculatedPayment > 0;
  }

  private getEventEndDate() {
    return EventUtils.getEventEndDate(this.onyxEvent, this.authenticationStore.isAgencyUser());
  }

  private prePaymentSubscription() {
    this.addSubscription(this.paymentStore.getPrepayment().subscribe(prepayment => {
      this.hasPrePayment = Boolean(prepayment);
    }));
  }

  disableConfirmBeforeAuthorizingPayment(): Observable<boolean> {
    const conditions = [
      this.isMissingVendorEmailContact(),
      this.isInvalidNonSubscriberVendorEmailContact(),
      this.isAutopayOn(),
      !this.isAuthorizedPayment(),
      !this.addPaymentEnabled,
      !this.isValidPaymentDate(),
      this.applyOoiv && this.isPendingTaxEstimated(),
      this.applyOoiv && !this.ooivRulesValidation,
      this.applyOoiv && !this.ooivEntityReady
    ];
    if (conditions.some(condition => condition)) { // if any of the conditions are true, disable the button
      return Observable.of(true);
    } else {
      return forkJoin<OnyxEvent, boolean>(
        this.eventStore.selectedEvent.take(1),
        this.paymentStore.isVendorPaymentError(this.vendor.eventPartyId).take(1)
      ).pipe(
        map(([
            selectedEvent,
            vendorPaymentError
          ]) => {
              if (this.calculatedPayment <= 0) {
                return true;
              } else if (!EventUtils.doesEventHaveAgency(selectedEvent)) {
                return true;
              } else if (vendorPaymentError) {
                return true;
              } else {
                return false;
              }
        })
      );
    }
  }

  private isMissingVendorEmailContact() {
    return !this.isNotMissingVendorEmailContact();
  }

  private isInvalidNonSubscriberVendorEmailContact() {
    return this.authenticationStore.hasPermission('EditEvent') && !this.vendor.subscriber
    && !PioneerValidators.isEmailFormatValid(this.vendor.contact.email);
  }

  private isPendingTaxEstimated() {
    return !this.isNotPendingTaxEstimated;
  }

  onNonCommissionTypeChange() {
    if (this.paymentForm && this.paymentForm.get('commissionAmount')) {
      if (this.isPayDirect()) {
        this.paymentForm.get('commissionAmount').setValue(this.calculatedPaymentWithTax.toFixed(2));
        this.paymentForm.get('commissionAmount').markAsTouched();
      } else {
        this.paymentForm.get('commissionAmount').setValue(null);
        this.paymentForm.get('commissionAmount').markAsUntouched();
      }
    }
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/payment/add-payment/add-payment.component.ts